import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Calendar } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { canAccess } from "@/lib/permissions";

const ACTIVE = new Set(["booked", "confirmed", "in_progress", "checked_in"]);

function localTodayRange() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const date = `${y}-${m}-${day}`;
    return { dateFrom: date, dateTo: `${date}T23:59:59` };
}

function formatTime(iso) {
    try {
        return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    } catch {
        return "";
    }
}

export default function TopBarNotifications({ role }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const enabled = canAccess(role, "calendar");

    const load = useCallback(async () => {
        if (!enabled) {
            setItems([]);
            return;
        }
        setLoading(true);
        try {
            const { dateFrom, dateTo } = localTodayRange();
            const { data } = await api.get("/appointments", {
                params: { date_from: dateFrom, date_to: dateTo },
            });
            const upcoming = (data || [])
                .filter((a) => ACTIVE.has(a.status))
                .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
            setItems(upcoming);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [enabled]);

    useEffect(() => {
        load();
        const id = setInterval(load, 5 * 60 * 1000);
        return () => clearInterval(id);
    }, [load]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const count = items.length;

    if (!enabled) return null;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="relative flex size-9 items-center justify-center rounded-full text-[#5C4033] transition-colors hover:bg-[#FAF9F6] hover:text-[#4A0E17]"
                    aria-label="Notifications"
                    data-testid="topbar-notifications"
                >
                    <Bell className="size-[18px]" strokeWidth={2} />
                    {count > 0 && (
                        <span className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-[#C84B31] text-[9px] font-bold text-white ring-2 ring-white">
                            {count > 9 ? "9+" : count}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[min(320px,calc(100vw-2rem))] rounded-xl border-[#E8E4DE] p-0 font-['Outfit',system-ui,sans-serif] shadow-[0_12px_40px_rgba(45,41,38,0.1)]"
            >
                <div className="border-b border-[#E8E4DE]/70 px-4 py-3">
                    <p className="text-sm font-bold text-[#2D2926]">Today&apos;s schedule</p>
                    <p className="text-xs font-medium text-[#8A8178]">Upcoming appointments</p>
                </div>
                {loading && (
                    <p className="px-4 py-4 text-xs font-medium text-[#8A8178]">Loading…</p>
                )}
                {!loading && items.length === 0 && (
                    <p className="px-4 py-6 text-center text-xs font-medium text-[#8A8178]">
                        No upcoming bookings today
                    </p>
                )}
                {!loading && items.length > 0 && (
                    <ul className="scrollbar-dialog max-h-72 overflow-y-auto overscroll-contain pr-1">
                        {items.map((a) => (
                            <li key={a.id} className="border-b border-[#E8E4DE]/50 last:border-0">
                                <button
                                    type="button"
                                    className="flex w-full items-start gap-2.5 px-4 py-3 text-left hover:bg-[#FAF9F6]"
                                    onClick={() => {
                                        setOpen(false);
                                        navigate("/app/calendar");
                                    }}
                                >
                                    <Calendar className="mt-0.5 size-4 shrink-0 text-[#C9A227]" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-semibold text-[#2D2926]">
                                            {a.customer?.full_name || "Walk-in"}
                                        </span>
                                        <span className="block text-xs text-[#8A8178]">
                                            {formatTime(a.start_at)}
                                            {(a.items || []).length > 0 && (
                                                <> · {(a.items[0].service_name || "Service")}</>
                                            )}
                                        </span>
                                        <span className="mt-0.5 inline-block rounded-full bg-[#FDF3DC] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#9A7B10]">
                                            {a.status}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="border-t border-[#E8E4DE]/70 p-2">
                    <button
                        type="button"
                        className="w-full rounded-lg py-2 text-xs font-semibold text-[#4A0E17] hover:bg-[#FAF9F6]"
                        onClick={() => {
                            setOpen(false);
                            navigate("/app/calendar");
                        }}
                    >
                        Open calendar
                    </button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
